import type { GetInputPropsReturnType } from "@mantine/form";
import { Alert, Box, Button, Stack, TextInput, UnstyledButton } from "@mantine/core";
import { IconAlertCircle, IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { FormEvent } from "react";
import type { MessageState } from "../useLoginActions";
import type { LoginTr } from "../useLoginTranslations";
import styles from "../LoginPage.module.css";
import { AuthSplitLayout } from "./AuthSplitLayout";
import layoutStyles from "./AuthSplitLayout.module.css";

export interface LoginForgotPasswordViewProps {
  emailInputProps: GetInputPropsReturnType;
  onBack: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  resetPasswordLoading: boolean;
  resetPasswordMessage: MessageState;
  error: string;
  logo?: ReactNode;
  tr: LoginTr;
}

export function LoginForgotPasswordView({
  emailInputProps,
  onBack,
  onSubmit,
  resetPasswordLoading,
  resetPasswordMessage,
  error,
  logo,
  tr,
}: LoginForgotPasswordViewProps) {
  return (
    <AuthSplitLayout brandMode="forgotPassword" logo={logo} tr={tr}>
      <UnstyledButton type="button" className={layoutStyles.backLink} onClick={onBack}>
        <IconArrowLeft size={16} aria-hidden />
        {tr("login.forgotPassword.backToLogin", "Back to log in")}
      </UnstyledButton>

      <Box component="h1" className={layoutStyles.formTitle} mb={0}>
        {tr("login.forgotPassword.title", "Reset your password")}
      </Box>
      <Box className={layoutStyles.formSubtitle}>
        {tr(
          "login.forgotPassword.subtitle",
          "Enter the email you use for Collective Discovery and we'll send you a reset link.",
        )}
      </Box>

      <form onSubmit={onSubmit}>
        <Stack gap="md">
          <TextInput
            className={styles.authInput}
            label={tr("login.form.emailLabel", "Email")}
            placeholder="you@company.com"
            {...emailInputProps}
            required
            withAsterisk
            autoComplete="email"
          />

          {error ? (
            <Alert icon={<IconAlertCircle size={16} />} title={tr("login.errorTitle", "Error")} color="red" radius="md">
              {error}
            </Alert>
          ) : null}

          {resetPasswordMessage ? (
            <Alert
              color={resetPasswordMessage.type === "success" ? "green" : "red"}
              radius="md"
              title={resetPasswordMessage.type === "success" ? undefined : tr("login.errorTitle", "Error")}
            >
              {resetPasswordMessage.text}
            </Alert>
          ) : null}

          <Button
            type="submit"
            fullWidth
            size="md"
            color="secondary"
            className={styles.ctaButton}
            rightSection={<IconArrowRight size={18} />}
            loading={resetPasswordLoading}
            disabled={resetPasswordLoading}
          >
            {resetPasswordLoading
              ? tr("login.forgotPassword.sending", "Sending...")
              : tr("login.forgotPassword.submit", "Send reset link")}
          </Button>

          {resetPasswordMessage?.type === "success" ? (
            <Box className={layoutStyles.infoCallout}>
              <IconAlertCircle size={20} color="var(--mantine-color-accent-filled)" style={{ flexShrink: 0 }} />
              <Box className={layoutStyles.infoCalloutText}>
                {tr(
                  "login.forgotPassword.infoHint",
                  "Check your inbox — the link expires in 30 minutes. If it doesn't arrive, look in spam or try again.",
                )}
              </Box>
            </Box>
          ) : null}
        </Stack>
      </form>
    </AuthSplitLayout>
  );
}
